import React, { FC } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import translate from '@translate';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import { colors, sizes } from 'src/themes/default';
import { BillOfMaterialsProductType } from 'src/types/billOfMaterials';
import ProductsSelect from 'src/components/ProductsSelect';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FormErrorsType } from '../../types/global';

export type EditKeysType = 'ID_PRODUCT' | 'COUNT';

const allColumns: TableColumnType[] = [
    {
        key: 'ID_PRODUCT',
        label: 'product',
        defaultShow: true,
        customRender: true,
    },
    {
        key: 'COUNT',
        label: 'count',
        type: 'number',
        defaultShow: true,
        customRender: true,
    },
];

type ProductsTablePropsType = {
    newBillsOfMaterials: boolean;
    data?: BillOfMaterialsProductType[];
    onChange: (value: any, error: boolean, errorKey?: string) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
};

const ProductsTable: FC<ProductsTablePropsType> = (props) => {
    const handleToggleRow = (row: TableRowType) => {
        if (row.ID < 0) {
            props.onChange(
                props.data?.filter((item) => item.ID != row.ID),
                false
            );
        } else {
            props.onChange(
                props.data?.map((item) => {
                    if (item.ID === row.ID) {
                        return { ...item, LINE_STATE: row.LINE_STATE === 'D' ? 'E' : 'D' };
                    }
                    return item;
                }),
                false
            );
        }
    };

    const getInputName = (id: number, key: string) => `billOfMaterials-${id}-${key}`;

    const changeValue = (key: EditKeysType, value: any, error: boolean, id: number) => {
        const newData = props.data?.map((BillOfMaterials) => {
            if (BillOfMaterials.ID === id) {
                const updatedBillOfMaterials = { ...BillOfMaterials };
                updatedBillOfMaterials[key] = value;
                return updatedBillOfMaterials;
            }
            return BillOfMaterials;
        });
        props.onChange(newData, error, getInputName(id, key));
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        switch (column.key) {
            case 'ID_PRODUCT':
                return (
                    <ProductsSelect
                        idKey="ID"
                        value={row.ID_PRODUCT}
                        error={props.editErrors[name]}
                        onChange={(value, error) => {
                            if (!Array.isArray(value)) {
                                changeValue('ID_PRODUCT', value?.ID, error, row.ID);
                            }
                        }}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'COUNT':
                return (
                    <TextFieldDebounced
                        value={row.COUNT}
                        error={props.editErrors[name]}
                        onChange={(value, error) =>
                            changeValue('COUNT', (value || value === 0) && String(value), error, row.ID)
                        }
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        required
                        disabled={props.disableEdit}
                    />
                );
        }
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="billOfMaterialsDetail.productsTable"
            allColumns={allColumns}
            initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
            rowActions={
                props.disableEdit
                    ? undefined
                    : [
                          {
                              icon: <HighlightOffIcon />,
                              label: translate('receivingDetail.productTable.action.removeProduct'),
                              onClick: handleToggleRow,
                              style: { height: sizes.table_action_with_input_H },
                              hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE !== 'E',
                          },
                          {
                              icon: <AddCircleIcon />,
                              label: translate('receivingDetail.productTable.action.addProduct'),
                              onClick: handleToggleRow,
                              hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'E',
                          },
                      ]
            }
            disableFilter
            customRender={customRender}
            rowSxFce={(row) =>
                row.LINE_STATE === 'D'
                    ? { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } }
                    : {}
            }
            sumCols={{ COUNT: true }}
            sumColsFilter={(row) => row.LINE_STATE === 'E'}
            maxHeight={'unset'}
        />
    );
};

export default ProductsTable;
